import memoizeOne from 'memoize-one'
import { formatNumberForDisplay } from "shared/imports/sharedHelperFunctions"

// TODO there are a lot of magic numbers related to size of things. we need to make those constants so
// we can play with them as needed rather than find them within the functions

// this serves as a wrapper to each d3 function. It takes in as its first argument
// what properties of the graph/data should be extracted from params to make a memoization
// key for the second argument, the function. One property that is used frequently is
// 'timestamp'. Timestamp, by convention, is the time at which data for this graph was fetched.
// As such, it is equivalent to `data`, but much easier/faster to serialize for the purposes
// of memoization.
export const memoize = (properties, func) => {
    const memoizedFunc = memoizeOne((memoKey, params) => func(params), (oldArgs, newArgs) => oldArgs[0] === newArgs[0])

    const toReturn = (params) => {
        const memoKey = (
            properties
                .map(
                    (prop) => {
                        let val = params[prop]
                        if (typeof val === 'object') {
                            if (val.length) {
                                // if tis an array, single concat the values
                                val = val.map(e => e).join(',')
                            } else {
                                // if its a dict, concat the values (keys might be the same like in colors)
                                val = Object.values(val).map(e => e).join(',')
                            }
                        }
                        return val
                    }
                )
                .join('-')
                + params.widgetKey
        )
        return memoizedFunc(memoKey, params)
    }
    toReturn._base = func
    return toReturn
}


// <<<<< GETTERS >>>>>

/**
 * getBounds
 * for 2d data (x, y), getBounds grabs the range and domain of the graph (max X val, max Y val, mins)
 *
 * returns: Object, with properties xMin, xMax, yMin, yMax
 */
export const getBounds = memoize(['timestamp', 'width'], (params) => {
    const { data } = params
    let xMin, xMax, yMin, yMax
    data.map(
        datum => (
            datum.forEach(
                (e) => {
                    const [x, y] = e
                    if (!xMin || x < xMin) {
                        xMin = x
                    }
                    if (!xMax || x > xMax) {
                        xMax = x
                    }

                    // DateFields sum_aggregation.querymethod
                    // app/ledger/models.py sum_aggregation_frequency_tuples
                    // returns float values (formatted as strings)
                    // because we are summing Transaction amounts (cents) into a total

                    // in order to prevent loss of $ precision, we convert to float here
                    if (!yMin || parseFloat(y) < parseFloat(yMin)) {
                        yMin = parseFloat(y)
                    }
                    if (!yMax || parseFloat(y) > parseFloat(yMax)) {
                        yMax = parseFloat(y)
                    }
                }
            )
        )
    )
    return { xMin, xMax, yMin, yMax }
})

/**
 * getPipRadius
 * based on how wide the graph is, and how much data there is, return how big a "pip" should be
 *
 * returns: number
 */
export const getPipRadius = memoize(['timestamp', 'width'], (params) => {
    const { data, width } = params
    const pips = Math.max(...data.map(e => e.length))
    const pixelsPerPip = Math.ceil(width / pips)

    const radius = Math.max(2, Math.floor(pixelsPerPip / 5))
    return Math.min(radius, 5)
})

/**
 * getTimeXScale
 * makes a d3 time scale proportional to the width and data boundaries
 *
 * returns: d3 scale
 */
export const getTimeXScale = memoize(['timestamp', 'width'], (params) => {
    const { width } = params
    const { xMin, xMax } = getBounds(params)
    return d3.time.scale().domain([xMin, xMax]).range([0, width])
})

/**
 * getYScale
 * makes a d3 linear scale proportional to the height and data boundaries
 *
 * returns: d3 scale
 */
export const getYScale = memoize(['timestamp', 'height'], (params) => {
    const { height } = params
    const { yMax } = getBounds(params)
    return d3.scale.linear().domain([0, yMax]).range([height, 0])
})

/**
 * getNumOfTicks
 * finds how many vertical and horizontal ticks the graph should have for its axis, based on its data and size
 *
 * returns: Object, with properties verticalTicks and horizontalTicks
 */
export const getNumOfTicks = memoize(['timestamp', 'height', 'width'], (params) => {
    const PIXELS_PER_DATAPOINT = 100
    const { data, width, height } = params
    const maxDataPoints = Math.max(...data.map(datum => datum.length))
    const spaceNeeded = maxDataPoints * PIXELS_PER_DATAPOINT // pixels per datapoint
    return {
        // if we return null ticks, d3 takes care of adding as many as it sees fit. That is good enough for now
        verticalTicks: Math.max(3, spaceNeeded > height ? Math.floor(height / PIXELS_PER_DATAPOINT) : maxDataPoints), // : null,
        horizontalTicks: spaceNeeded > width ? Math.floor(width / PIXELS_PER_DATAPOINT) : maxDataPoints, // : null,
    }
})

// <<<<<< RENDERERS >>>>>>
// renderers have the job of cleaning what it will draw before running, to achieve pseudo-idempotency

/**
 * renderTimeXAxis
 * creates an x axis for time-based x data (height is needed to push the axis below the body of the graph)
 *
 * renders: svg > g > g.axis-bottom
 */
export const renderTimeXAxis = memoize(['timestamp', 'height', 'width'], (params) => {
    // clean up
    const { g, height, timeFormat } = params
    g.select('g.axis-bottom').remove()

    // prepare
    const x = getTimeXScale(params)
    const { horizontalTicks } = getNumOfTicks(params)

    const axis = (
        d3
        .svg
        .axis()
        .orient('bottom')
        .scale(x)
        .ticks(horizontalTicks)
        .tickSize(0)
        .tickFormat(d3.time.format(timeFormat))
    )

    // render
    g
        .append('g')
        .attr('class', 'axis-bottom')
        .call(axis)
        .attr('transform', `translate(0, ${height + 10})`)
})

/**
 * renderYAxis
 * creates an y axis for ordinal y data, on the left side of the graph
 *
 * renders: svg > g > g.axis-left
 */
export const renderYAxis = memoize(['timestamp', 'height'], (params) => {
    // clean up
    const { g } = params
    g.select('g.axis-left').remove()

    // prepare
    const y = getYScale(params)
    const { verticalTicks } = getNumOfTicks(params)

    const axis = d3.svg.axis().orient('left').scale(y).ticks(verticalTicks).tickSize(0).tickFormat(yValue => formatNumberForDisplay(yValue))

    // render
    g
        .append('g')
        .attr('class', 'axis-left')
        .call(axis)
        .attr('transform', 'translate(-10, 0)')
})

/**
 * renderHorizontalGridlines
 * creates grid lines going left to right, at the place where y axis ticks would go
 *
 * renders: svg > g > g.grid-left
 */
export const renderHorizontalGridlines = memoize(['timestamp', 'height', 'width'], (params) => {
    // clean up
    const { g, width } = params
    g.select('g.grid-left').remove()

    // prepare
    const y = getYScale(params)
    const { verticalTicks } = getNumOfTicks(params)

    const axis = d3.svg.axis().orient('left').scale(y).ticks(verticalTicks).tickSize(-width, 0).tickFormat('')

    // render
    g
        .append('g')
        .attr('class', 'grid-left')
        .call(axis)
        .call(g_ => g_.select('.domain').remove())
        .style('fill', 'none')
        .style('stroke', '#d2d2d2')
        .style('shape-rendering', 'crispEdges')
})

/**
 * renderLine
 * draws a continuous line connecting each data point, for each dataset, to scale on the graph
 *
 * renders: svg > g > path.line
 */
export const renderLine = memoize(['timestamp', 'width', 'height', 'colors'], (params) => {
    // clean up
    const { g, data, colors } = params
    g.selectAll('path.line').remove()

    // prepare
    const x = getTimeXScale(params)
    const y = getYScale(params)
    const pipRadius = getPipRadius(params)

    const line = d3.svg.line().x(d => x(d[0])).y(d => y(d[1]))

    // this can be broken down per line/index, but that would overcomplicate this
    // render
    data.forEach(
        (datum, index) => {
            g
                .append('path')
                .attr('class', `line line-${index}`)
                .attr('d', () => line(datum))
                .attr('fill', 'none')
                .attr('stroke', colors && colors[index] || '#503ABD')
                .attr('stroke-width', `${pipRadius - 0.5}px`)
        }
    )
})

/**
 * renderPips
 * draws a single dot (with a white border) for each data point
 *
 * renders: svg > g > circle
 */
export const renderPips = memoize(['timestamp', 'width', 'height', 'colors'], (params) => {
    // clean up
    const { g, data, colors } = params
    g.selectAll('circle').remove()

    // prepare
    const x = getTimeXScale(params)
    const y = getYScale(params)
    const pipRadius = getPipRadius(params)

    // render
    data.forEach(
        (datum, index) => {
            g
                .selectAll('circles')
                .data(datum)
                .enter()
                .append('circle')
                .attr('fill', 'white')
                .attr('stroke', 'none')
                .attr('cx', d => x(d[0]))
                .attr('cy', d => y(d[1]))
                .attr('r', pipRadius + 1)

            g
                .selectAll('circles')
                .data(datum)
                .enter()
                .append('circle')
                .attr('fill', colors && colors[index] || '#503ABD')
                .attr('stroke', 'none')
                .attr('cx', d => x(d[0]))
                .attr('cy', d => y(d[1]))
                .attr('r', pipRadius)
        }
    )
})

/**
 * renderLegend
 * puts a simple legend for each dataset below the x axis
 *
 * renders: svg > rect.legend, svg > text.legend
 */
export const renderLegend = memoize(['labels', 'originalColors', 'height'], (params) => {
    // goes slightly out of convention and interacts with the top level svg
    // clean up
    const {
        svg,
        labels,
        height,
        originalColors,
    } = params
    svg.selectAll('rect.legend').remove()
    svg.selectAll('text.legend').remove()

    // prepare
    let legendX = 50

    // render
    labels.forEach(
        (label, index) => {
            svg
                .append('rect')
                .attr('class', 'legend')
                .attr('y', height + 72)
                .attr('x', legendX)
                .attr('height', 13)
                .attr('width', 13)
                .attr('rx', 3)
                .style('fill', originalColors && originalColors[index] || '#503ABD')

            svg
                .append('text')
                .attr('class', 'legend')
                .attr('y', height + 80)
                .attr('x', legendX + 18)
                .text(label || '')
                .style('font-size', '15px')
                .style('font-style', 'italic')
                .attr('alignment-baseline', 'middle')

            legendX += label ? (label.length * 7.5 + 40) : 40
        }
    )
})

/**
 * renders: svg > g.hover-circle, svg > g.hover-label, svg > path.hover-line, svg > g > rect.hover-base
 */
export const renderHoverState = memoize(['timestamp', 'width', 'height', 'editing'], (params) => {
    // clean up
    const {
        g,
        svg,
        data,
        width,
        height,
        editing,
    } = params
    svg.selectAll('circle.hover-circle').remove()
    svg.selectAll('rect.hover-rect').remove()
    svg.selectAll('text.hover-label').remove()
    svg.select('path.hover-line').remove()
    g.select('rect.hover-base').remove()

    const x = getTimeXScale(params)
    const y = getYScale(params)
    const pipRadius = getPipRadius(params)
    // Create the circle that travels along the curve of chart

    const rectWidth = 40
    const rectHeight = 33
    const hoverRects = (
        data
            .filter(e => e.length)
            .map(() => {
                const rectG = svg.append('g')
                const filter = rectG
                    .append('filter')
                    .attr('id', 'blurMe')
                    .append('feGaussianBlur')
                    .attr('stdDeviation', 2)

                const shadow = rectG
                    .append('rect')
                    .attr('class', 'hover-rect')
                    .attr("rx", 4)
                    .attr("ry", 4)
                    .attr("width", rectWidth)
                    .attr("height", rectHeight)
                    .attr('filter', 'url(#blurMe)')
                    .style('fill', 'gray')
                    .style('opacity', 0)

                const rect = rectG
                    .append('rect')
                    .attr('class', 'hover-rect')
                    .attr("rx", 4)
                    .attr("ry", 4)
                    .attr("width", rectWidth)
                    .attr("height", rectHeight)
                    .style('fill', 'white')
                    .style('opacity', 0)
                    .style('stroke-width', 0)

                return [shadow, rect]
            })
    )


    const hoverLabels = (
        data
            .filter(e => e.length)
            .map(() =>
                svg
                    .append('g')
                    .append('text')
                    .attr('class', 'hover-label')
                    .attr('text-anchor', 'middle')
                    .attr('dominant-baseline', 'middle')
                    .style('opacity', 0)
                    .style("font-size", "15px")
            )
    )

    const hoverLine = (
        svg
            .append('path')
            .attr('class', 'mouse-line, hover-line')
            .attr('transform', 'translate(0, 30)')
            .style('stroke', '#687273')
            .style('stroke-width', 1)
            .style('opacity', 0)
    )

    const hoverCircles = (
        data
            .filter(e => e.length)
            .map(() =>
                svg
                .append('circle')
                .attr('class', 'hover-circle')
                .attr('fill', 'white')
                .attr('stroke', 'none')
                .attr('r', pipRadius - 2)
                .style('opacity', 0)
            )
    )

    // What happens when the mouse move -> show the annotations at the right positions.
    const mouseover = () => {
        if (editing) {
            return
        }
        svg.selectAll('.hover-line').style('opacity', 1)
    }
    const mouseout = () => {
        svg.selectAll('.hover-line').style('opacity', 0)
        svg.selectAll('.hover-rect').style('opacity', 0)
        svg.selectAll('.hover-label').style('opacity', 0)
        svg.selectAll('.hover-circle').style('opacity', 0)
    }

    const mousemove = () => {
        if (editing) {
            return
        }
        // allllrighty this is gonna be a thing.
        // first create the bisect functions, left and right
        const bisectors = d3.bisector(d => d[0])
        // grab the mouse position (not an arrow function so `this` is bound to the mousemove element),
        // and then grab what the x value would've been for said position
        const xPos = x.invert(d3.mouse(d3.event.target)[0])

        // then, use bisect to find the (index, value) for the closest match to a data point to the
        // above xPos(ition), checking if it's to the left or to the right, for each datum
        let nearestX = []
        let currentDiff = null
        data.forEach((datum, i) => {
            if (datum.length < 1) {
                return
            }

            let rightIndex = bisectors.right(datum, xPos)
            let leftIndex = rightIndex - 1

            let nearestIndex
            let nearestForThisDatum
            if (!datum[rightIndex]) {
                // if we are too far right, just go ahead and grab the rightmost one
                rightIndex = datum.length - 1
                leftIndex = datum.length - 2
            }
            const rightDiff = Math.abs(xPos - datum[rightIndex][0])

            if (leftIndex < 0) {
                nearestIndex = rightIndex
                nearestForThisDatum = rightDiff
            } else {
                const leftDiff = Math.abs(xPos - datum[leftIndex][0])
                nearestIndex = leftDiff <= rightDiff ? leftIndex : rightIndex
                nearestForThisDatum = leftDiff <= rightDiff ? leftDiff : rightDiff
            }

            if (currentDiff === null || nearestForThisDatum < currentDiff) {
                nearestX = [{ datumI: i, index: nearestIndex }]
                currentDiff = nearestForThisDatum
            } else if (nearestForThisDatum === currentDiff) {
                nearestX.push({ datumI: i, index: nearestIndex })
            }
        })

        let lastHeight
        const placementDiff = 25
        const highlighted = nearestX.map(e => e.datumI)
        nearestX.forEach(({ datumI, index }) => {
            const dataPoint = data[datumI][index]

            // get the inital position for the label where the data point is (with offset)
            const cx = x(dataPoint[0]) + 50
            const cy = y(dataPoint[1]) + 30

            let baseX = cx + placementDiff
            // write the label text and reveal it (should be at half the height of the label itself)
            hoverLabels[datumI]
                .html(dataPoint[1])
                .attr('y', cy - placementDiff + (rectHeight / 2) + 3)
                .style('opacity', 1)

            // grab the text width and find out how long our label rect needs to be
            const labelWidth = hoverLabels[datumI].node().getBoundingClientRect().width
            const thisRectWidth = labelWidth + 20 > rectWidth ? labelWidth + 20 : rectWidth

            // if we have another label that would be on the same space OR
            // we are furhter to the right than the width of the graph itself
            if (
                (lastHeight && Math.abs(lastHeight - (cy - placementDiff)) < rectWidth)
                || ((baseX - placementDiff) > width)
            ) {
                // then go ahead and flip the labels offset to the left side of the data point
                baseX -= ((2 * placementDiff) + thisRectWidth)
            }

            hoverLabels[datumI]
                .attr('x', baseX + (thisRectWidth / 2))

            hoverRects[datumI][0]
                .attr('x', baseX)
                .attr('y', cy - placementDiff)
                .style('opacity', 0.75)
                .attr('width', thisRectWidth)

            hoverRects[datumI][1]
                .attr('x', baseX)
                .attr('y', cy - placementDiff)
                .style('opacity', 1)
                .attr('width', thisRectWidth)

            lastHeight = cy - placementDiff

            hoverCircles[datumI]
                .attr('cx', cx)
                .attr('cy', cy)
                .style('opacity', 1)
        })

        data.forEach((datum, i) => {
            if (!highlighted.includes(i)) {
                hoverRects[i][0].style('opacity', 0)
                hoverRects[i][1].style('opacity', 0)
                hoverLabels[i].style('opacity', 0)
                hoverCircles[i].style('opacity', 0)
            }
        })

        if (nearestX.length) {
            const xForLine = x(data[nearestX[0].datumI][nearestX[0].index][0]) + 50
            hoverLine
                .attr('d', () => `M${xForLine},${height} ${xForLine},0`)
        }
    }

    g
        .append('rect')
        .on('mouseout', mouseout)
        .on('mouseover', mouseover)
        .on('mousemove', mousemove)
        .attr('class', 'hover-base')
        .attr('width', width)
        .attr('height', height)
        .style('fill', 'none')
        .style('pointer-events', 'all')
})
